import { appPath } from '@featit/shared/addressing/paths';

/**
 * AC-19.11 (`docs/concept-mobile-app.md`, I-1297): how the More drawer's New work area asks for the
 * Create work area dialog over the content screen, from the drawer's own small web view.
 *
 * ⭐ **A path.** The row names the content screen's own address with this intent on it and posts
 * the `drawerNavigate` every shell already knows, so no message type and no capability is new, and
 * the shipped app opens it just the same. `Layout` reads the intent from the
 * address, opens the dialog over the screen, and replaces the address with the plain one — which
 * the shell then hears as that screen, so it keeps no address that would ask again.
 */
export const NEW_WORK_AREA_PARAM = 'new-work-area';

/** The intent's value for the person's own namespace; any other value is a namespace's id. */
const OWN_NAMESPACE = '1';

/** Where the dialog opens when no content screen's address is known: Agents needs no work area. */
export const NEW_WORK_AREA_FALLBACK_PATH = appPath('/agents');

export interface INewWorkAreaIntent {
  /** The address without the intent: where the dialog opens. */
  readonly path: string;
  /** The namespace asked for; `null` is the person's own. */
  readonly namespaceId: string | null;
}

function parts(path: string): { pathname: string; params: URLSearchParams; hash: string } {
  const hashAt = path.indexOf('#');
  const hash = hashAt === -1 ? '' : path.slice(hashAt);
  const rest = hashAt === -1 ? path : path.slice(0, hashAt);
  const queryAt = rest.indexOf('?');
  return {
    pathname: queryAt === -1 ? rest : rest.slice(0, queryAt),
    params: new URLSearchParams(queryAt === -1 ? '' : rest.slice(queryAt + 1)),
    hash,
  };
}

function join(pathname: string, params: URLSearchParams, hash: string): string {
  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ''}${hash}`;
}

/** `path` (a pathname with its search) carrying the intent, whatever else its search holds. */
export function newWorkAreaIntentPath(path: string, namespaceId: string | null = null): string {
  const { pathname, params, hash } = parts(path);
  params.set(NEW_WORK_AREA_PARAM, namespaceId ?? OWN_NAMESPACE);
  return join(pathname, params, hash);
}

/** The intent `path` carries and the address without it; `null` for a path that carries none. */
export function takeNewWorkAreaIntent(path: string): INewWorkAreaIntent | null {
  const { pathname, params, hash } = parts(path);
  const value = params.get(NEW_WORK_AREA_PARAM);
  if (value === null) return null;
  params.delete(NEW_WORK_AREA_PARAM);
  return {
    path: join(pathname, params, hash),
    namespaceId: value === OWN_NAMESPACE || value === '' ? null : value,
  };
}

let contentPath: string | null = null;

/**
 * The drawer's panel is told the path of the screen under it as the drawer opens (`scope`, AC-18.8).
 * Kept so the panel's New work area opens the dialog over that screen and moves nobody.
 */
export function noteContentPath(path: string | null): void {
  contentPath = path;
}

/** The path the drawer's panel asks the shell to open for New work area. */
export function drawerNewWorkAreaPath(namespaceId: string | null = null): string {
  const inApp =
    contentPath !== null && contentPath.startsWith('/') && !contentPath.startsWith('//');
  const base = inApp && contentPath ? contentPath : NEW_WORK_AREA_FALLBACK_PATH;
  // A screen that was itself opened for the intent is asked once, not twice.
  return newWorkAreaIntentPath(takeNewWorkAreaIntent(base)?.path ?? base, namespaceId);
}
